import { useReducer, useMemo } from "react"
import type { Puzzle } from "@shared/types.ts"
import { buildCellClueMap, getWordCells, type CellClueInfo } from "../lib/grid-utils.ts"

export interface PuzzleState {
  puzzle: Puzzle | null
  cells: Record<string, string> // "row,col" -> letter
  currentCell: [number, number] | null
  direction: "across" | "down"
  checkedCells: Record<string, "correct" | "incorrect">
  revealedCells: Set<string>
  flashCells: Record<string, "success" | "error">
  isComplete: boolean
}

export type PuzzleAction =
  | { type: "SET_PUZZLE"; puzzle: Puzzle }
  | { type: "SET_CELL"; row: number; col: number; letter: string }
  | { type: "CLEAR_CELL"; row: number; col: number }
  | { type: "SELECT_CELL"; row: number; col: number }
  | { type: "TOGGLE_DIRECTION" }
  | { type: "SET_DIRECTION"; direction: "across" | "down" }
  | { type: "CHECK_WORD"; wordCells: [number, number][]; solution: string[][] }
  | { type: "CHECK_ALL"; solution: string[][] }
  | { type: "REVEAL_WORD"; wordCells: [number, number][]; solution: string[][] }
  | { type: "CLEAR_ALL" }
  | { type: "SET_FLASH"; cells: Record<string, "success" | "error"> }
  | { type: "CLEAR_FLASH" }
  | {
      type: "RESTORE_PROGRESS"
      cells: Record<string, string>
      checkedCells: Record<string, "correct" | "incorrect">
      revealedCells: string[]
      currentCell: [number, number] | null
      direction: "across" | "down"
      isComplete: boolean
    }

const initialState: PuzzleState = {
  puzzle: null,
  cells: {},
  currentCell: null,
  direction: "across",
  checkedCells: {},
  revealedCells: new Set(),
  flashCells: {},
  isComplete: false,
}

function checkCompletion(cells: Record<string, string>, puzzle: Puzzle): boolean {
  const grid = puzzle.grid
  const solution = puzzle.solution
  for (let r = 0; r < puzzle.gridSize; r++) {
    for (let c = 0; c < puzzle.gridSize; c++) {
      if (grid[r]![c]!.black) continue
      const key = `${r},${c}`
      const userLetter = cells[key]
      const solutionLetter = solution[r]![c]
      if (!userLetter || userLetter !== solutionLetter) return false
    }
  }
  return true
}

function puzzleReducer(state: PuzzleState, action: PuzzleAction): PuzzleState {
  switch (action.type) {
    case "SET_PUZZLE":
      return {
        ...initialState,
        puzzle: action.puzzle,
        currentCell: [0, 0],
        direction: "across",
      }

    case "SET_CELL": {
      const key = `${action.row},${action.col}`
      const newCells = { ...state.cells, [key]: action.letter.toUpperCase() }
      const newChecked = { ...state.checkedCells }
      delete newChecked[key] // Clear check status when cell is edited
      const isComplete = state.puzzle ? checkCompletion(newCells, state.puzzle) : false
      return { ...state, cells: newCells, checkedCells: newChecked, isComplete }
    }

    case "CLEAR_CELL": {
      const key = `${action.row},${action.col}`
      const newCells = { ...state.cells }
      delete newCells[key]
      const newChecked = { ...state.checkedCells }
      delete newChecked[key]
      return { ...state, cells: newCells, checkedCells: newChecked, isComplete: false }
    }

    case "SELECT_CELL":
      return { ...state, currentCell: [action.row, action.col] }

    case "TOGGLE_DIRECTION":
      return { ...state, direction: state.direction === "across" ? "down" : "across" }

    case "SET_DIRECTION":
      return { ...state, direction: action.direction }

    case "CHECK_WORD": {
      const newChecked = { ...state.checkedCells }
      for (const [r, c] of action.wordCells) {
        const key = `${r},${c}`
        const userLetter = state.cells[key]
        if (userLetter) {
          const correct = userLetter === action.solution[r]![c]
          newChecked[key] = correct ? "correct" : "incorrect"
        }
      }
      return { ...state, checkedCells: newChecked }
    }

    case "CHECK_ALL": {
      const newChecked: Record<string, "correct" | "incorrect"> = {}
      if (state.puzzle) {
        for (let r = 0; r < state.puzzle.gridSize; r++) {
          for (let c = 0; c < state.puzzle.gridSize; c++) {
            if (state.puzzle.grid[r]![c]!.black) continue
            const key = `${r},${c}`
            const userLetter = state.cells[key]
            if (userLetter) {
              const correct = userLetter === action.solution[r]![c]
              newChecked[key] = correct ? "correct" : "incorrect"
            }
          }
        }
      }
      return { ...state, checkedCells: newChecked }
    }

    case "REVEAL_WORD": {
      const newCells = { ...state.cells }
      const newChecked = { ...state.checkedCells }
      const newRevealed = new Set(state.revealedCells)
      for (const [r, c] of action.wordCells) {
        const key = `${r},${c}`
        newCells[key] = action.solution[r]![c]!
        newChecked[key] = "correct"
        newRevealed.add(key)
      }
      const isComplete = state.puzzle ? checkCompletion(newCells, state.puzzle) : false
      return { ...state, cells: newCells, checkedCells: newChecked, revealedCells: newRevealed, isComplete }
    }

    case "CLEAR_ALL":
      return {
        ...state,
        cells: {},
        checkedCells: {},
        revealedCells: new Set(),
        flashCells: {},
        isComplete: false,
      }

    case "SET_FLASH":
      return { ...state, flashCells: action.cells }

    case "CLEAR_FLASH":
      return { ...state, flashCells: {} }

    case "RESTORE_PROGRESS":
      return {
        ...state,
        cells: action.cells,
        checkedCells: action.checkedCells,
        revealedCells: new Set(action.revealedCells),
        currentCell: action.currentCell,
        direction: action.direction,
        isComplete: action.isComplete,
      }

    default:
      return state
  }
}

export function usePuzzleState() {
  const [state, dispatch] = useReducer(puzzleReducer, initialState)

  const cellClueMap = useMemo(() => {
    if (!state.puzzle) return new Map<string, CellClueInfo>()
    return buildCellClueMap(state.puzzle)
  }, [state.puzzle])

  return { state, dispatch, cellClueMap }
}
